import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

type Metric = { label: string; value: number | string; alert?: boolean };
type RemoteState = { loading: boolean; error: boolean; metrics: Metric[] };

interface Props {
  casa?: { checkIns: number; checkOuts: number; cleaningPending: number };
}

const initial: RemoteState = { loading: true, error: false, metrics: [] };

export default function BusinessSummary({ casa }: Props) {
  const [casaRemote, setCasaRemote] = useState<{ checkIns: number; checkOuts: number; cleaningPending: number }>({ checkIns: 0, checkOuts: 0, cleaningPending: 0 });
  const [rental, setRental] = useState<RemoteState>(initial);
  const [sales, setSales] = useState<RemoteState>(initial);
  const [sim, setSim] = useState<RemoteState>(initial);
  const [goldys, setGoldys] = useState<RemoteState>(initial);

  useEffect(() => {
    const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    void supabase.rpc('get_goldys_daily_summary', { p_date: today }).then(({ data, error }) => {
      if (error || !data || data.authorized === false) {
        setGoldys({ loading: false, error: true, metrics: [] });
        return;
      }
      setGoldys({ loading: false, error: false, metrics: [
        { label: '本日組数', value: Number(data.party_count || 0) },
        { label: '本日売上', value: `¥${Number(data.gross_sales || 0).toLocaleString()}` },
        { label: '現金', value: `¥${Number(data.cash_sales || 0).toLocaleString()}` },
      ] });
    });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const request = async (url: string) => {
        const response = await fetch(url, { headers: { Authorization: `Bearer ${session.access_token}` }, signal: controller.signal });
        if (!response.ok) throw new Error('summary unavailable');
        return response.json();
      };
      const results = await Promise.allSettled([
        request('https://ygdxldiivuqpecdrjtjg.supabase.co/functions/v1/staff-business-summary'),
        request('https://brgynbjpixiauhsuxdio.supabase.co/functions/v1/staff-business-summary'),
        request('https://ynhldsmuuzdnzlahexdi.supabase.co/functions/v1/staff-business-summary'),
      ]);
      if (controller.signal.aborted) return;
      const [rentalResult, salesResult, simResult] = results;
      if (rentalResult.status === 'fulfilled') {
        const d = rentalResult.value;
        setRental({ loading: false, error: false, metrics: [
          { label: '本日貸出', value: d.pickups_today }, { label: '本日返却', value: d.returns_today },
          { label: '貸出中', value: d.active }, { label: '要確認', value: d.attention, alert: d.attention > 0 },
        ] });
      } else setRental({ loading: false, error: true, metrics: [] });
      if (salesResult.status === 'fulfilled') {
        const d = salesResult.value;
        setSales({ loading: false, error: false, metrics: [
          { label: '未読問合せ', value: d.unread_leads, alert: d.unread_leads > 0 },
          { label: '進行中', value: d.active_sales }, { label: '成約', value: d.completed_sales },
        ] });
      } else setSales({ loading: false, error: true, metrics: [] });
      if (simResult.status === 'fulfilled') {
        const d = simResult.value;
        setSim({ loading: false, error: false, metrics: [
          { label: '本日販売', value: d.sold_today }, { label: '今月販売', value: d.sold_month },
          { label: '未配信', value: d.pending_delivery, alert: d.pending_delivery > 0 },
        ] });
      } else setSim({ loading: false, error: true, metrics: [] });
    };
    void load();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (casa) return;
    const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    Promise.all([
      supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('arrival_date', today).neq('booking_status', 'cancelled'),
      supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('departure_date', today).neq('booking_status', 'cancelled'),
      supabase.from('housekeeping_tasks').select('id', { count: 'exact', head: true }).eq('task_date', today).not('status', 'in', '("completed","ready","not_required")'),
    ]).then(([ins, outs, cleaning]) => setCasaRemote({ checkIns: ins.count || 0, checkOuts: outs.count || 0, cleaningPending: cleaning.count || 0 }));
  }, [casa]);

  const casaSummary = casa || casaRemote;

  const cards: Array<{ name: string; url: string; icon: string; state?: RemoteState; metrics?: Metric[]; note?: string }> = [
    { name: 'Casa Kokoro', url: '/casa', icon: 'ri-hotel-line', metrics: [
      { label: 'IN', value: casaSummary.checkIns }, { label: 'OUT', value: casaSummary.checkOuts },
      { label: '清掃待ち', value: casaSummary.cleaningPending, alert: casaSummary.cleaningPending > 0 },
    ] },
    { name: "Goldy’s", url: '/goldys-closing', icon: 'ri-restaurant-line', state: goldys },
    { name: 'HanaMomo Express', url: 'https://hanamomoexpress.com/admin', icon: 'ri-taxi-line', note: '予約集計は接続準備中' },
    { name: 'Car Rental', url: 'https://hanamomocar.com/admin', icon: 'ri-car-line', state: rental },
    { name: 'Car Sales', url: 'https://hanamomocars.com/admin', icon: 'ri-roadster-line', state: sales },
    { name: 'HanaMomo SIM', url: 'https://hanamomosim.com/admin', icon: 'ri-sim-card-line', state: sim },
  ];

  return (
    <div className="rounded-md border overflow-hidden" style={{ backgroundColor: '#FFFEFA', borderColor: '#DED9CC' }}>
      <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #DED9CC' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#6B6B67' }}>全事業サマリー</h3>
        <span className="text-[10px]" style={{ color: '#8A8983' }}>カードから管理画面へ</span>
      </div>
      <div className="p-3 space-y-1.5">
        {cards.map((card) => {
          const metrics = card.metrics || card.state?.metrics || [];
          const external = card.url.startsWith('http');
          return (
            <a key={card.name} href={card.url} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} className="block rounded-md border px-3 py-2" style={{ borderColor: '#DED9CC', backgroundColor: '#F8F6F0', color: '#252824' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#E8EDE9', color: '#234638' }}><i className={`${card.icon} text-xs`} /></span>
                <span className="text-xs font-semibold flex-1">{card.name}</span>
                <i className="ri-arrow-right-up-line text-xs" style={{ color: '#8A8983' }} />
              </div>
              {card.state?.loading ? <p className="text-[10px]" style={{ color: '#8A8983' }}>集計中…</p>
                : card.state?.error ? <p className="text-[10px]" style={{ color: '#A33A32' }}>集計を取得できません</p>
                : card.note ? <p className="text-[10px]" style={{ color: '#8A8983' }}>{card.note}</p>
                : <div className="flex flex-wrap gap-x-3 gap-y-1">{metrics.map((metric) => <span key={metric.label} className="text-[10px]" style={{ color: metric.alert ? '#A33A32' : '#6B6B67' }}>{metric.label} <strong className="text-xs" style={{ color: metric.alert ? '#A33A32' : '#252824' }}>{metric.value}</strong></span>)}</div>}
            </a>
          );
        })}
      </div>
    </div>
  );
}
